import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import {
  RequireFeatureRoute,
  type UpgradeRequest,
} from '@hapana/shared/web/components/shared/feature-gate'
import type { FeatureKey } from '@hapana/shared/schemas/entitlements/feature-key.schema'

import { useSupportAssistant } from '_components/support-assistant/support-assistant.hooks'
import { useCoreEntitlements } from '_hooks/use-core-entitlements'

interface FeatureGatedRouteProps {
  feature: FeatureKey | FeatureKey[]
  /** Require ALL of `feature` (default: ANY). */
  requireAll?: boolean
}

/**
 * Route element that gates its child routes on the current site's entitlements.
 *
 * Entitled → renders the child routes; not entitled → renders the upgrade-prompt screen. Its
 * Upgrade button opens the support sidebar on a plan-change request that already says which
 * feature the brand wants, the same way the trial banner does. Use as the `element` of a parent
 * route whose children are the routes being gated, so the shared `RequireFeatureRoute` can render
 * them through its `<Outlet />`.
 */
export const FeatureGatedRoute = ({ feature, requireAll }: FeatureGatedRouteProps) => {
  const { t } = useTranslation('core')
  const entitlements = useCoreEntitlements()
  const { setPrefillIssueType, setPrefillDescription, setActivePanel, setActiveTab, setIsOpen } =
    useSupportAssistant()

  const requestUpgrade = useCallback(
    ({ featureName, upgradeMessage }: UpgradeRequest) => {
      setPrefillIssueType('PLANS')
      setPrefillDescription(
        t('core:featureGate.upgradeRequest', {
          feature: featureName,
          page: window.location.pathname,
          plan: upgradeMessage,
        }).trim()
      )
      setActivePanel('assistant')
      setActiveTab('support')
      setIsOpen(true)
    },
    [t, setPrefillIssueType, setPrefillDescription, setActivePanel, setActiveTab, setIsOpen]
  )

  return (
    <RequireFeatureRoute
      feature={feature}
      entitlements={entitlements}
      requireAll={requireAll}
      onUpgrade={requestUpgrade}
    />
  )
}
